import React from 'react';
import { format } from 'date-fns';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { ResponseActions } from '../../support/response/ResponseActionButton';
import {
  ResponseHead,
  ResponseIntro,
  ResponsePanel,
  ResponseReply
} from '../../support/response/responsePrimitives';
import { responseType, rsColor, rsv } from '../../support/response/responseScale';
import { SupportAction } from '../../support/types';
import { OrbitActionButton, OrbitActionFieldValue, OrbitActionStep } from '../types';
import L4StatusLine from './L4StatusLine';

// 6567:2217-2234: 35-tall rows ruled in the border, labels 40 in, values in a fixed column.
const Row = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: ${rsv.size('row')}px;
  margin: 0 15px 0 13px;
  padding: ${rsv.space('row')};
  padding-left: ${rsv.space('rowInset')}px;
  border-bottom: 1px solid ${rsColor.border};
  ${responseType('body')}

  > dt {
    flex: 0 0 68px;
    color: ${rsColor.secondary};
  }

  > dd {
    min-width: 0;
    margin: 0 0 0 ${rsv.space('rowGap')}px;
    color: ${rsColor.text};
    font-weight: 700;
    overflow-wrap: anywhere;
  }
`;

const Was = styled.span`
  color: ${rsColor.muted};
  font-weight: 400;
  text-decoration: line-through;
`;

const Arrow = styled.span`
  margin: 0 6px;
  color: ${rsColor.muted};
`;

const fieldLabel = (key: string): string => {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const FieldValue: React.FC<{ value: OrbitActionFieldValue }> = ({ value }) => {
  if (value !== null && typeof value === 'object') {
    return (
      <>
        <Was data-testid="orbit-l4-field-current">{String(value.current ?? '')}</Was>
        <Arrow aria-hidden="true">→</Arrow>
        <span data-testid="orbit-l4-field-next">{String(value.next ?? '')}</span>
      </>
    );
  }
  return <>{String(value ?? '')}</>;
};

const Rows = styled.dl`
  margin: 0;
`;

// 6566:9708: 21 tall, radius 8, outlined; the active look is the drawn one, the rest are neutral.
const StatePill = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  height: 21px;
  margin-left: 8px;
  padding: 0 8px;
  border: 1px solid ${({ $active }) => ($active ? '#91D5AD' : rsColor.muted)};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? '#15382D' : 'rgba(100, 112, 154, 0.14)')};
  color: ${({ $active }) => ($active ? '#16834F' : rsColor.secondary)};
  font-size: 10px;
  font-weight: 800;
  line-height: 15.2px;
  text-transform: capitalize;
`;

// 6567:2233: the button 24 under the last rule and 23 above the panel's edge; the action row adds
// its own button gap on top, so the padding is short by that gap.
const Footer = styled.div`
  padding: calc(24px - ${rsv.space('buttonGap')}px) 13px 23px;

  > [data-testid='orbit-response-actions'] {
    margin-top: 0;
  }
`;

const Note = styled.p<{ $tone?: 'error' }>`
  margin: 8px 0 0;
  color: ${({ $tone }) => ($tone === 'error' ? rsColor.danger : rsColor.secondary)};
  ${responseType('body')}
`;

/** Confirm leads; a card without one leads with its first button. */
const buttonKind = (id: string, index: number, all: OrbitActionButton[]): SupportAction['kind'] => {
  const lead = all.some(button => button.id === 'confirm') ? id === 'confirm' : index === 0;
  return lead ? 'primary' : 'secondary';
};

export type L4ConfirmCardMode = 'open' | 'working' | 'locked' | 'confirmed' | 'cancelled';

interface IL4ConfirmCardProps {
  text: string;
  step: OrbitActionStep;
  expiresAt: number | null;
  mode: L4ConfirmCardMode;
  failed?: boolean;
  /** The payload's buttons; the v1 card's own Confirm when absent. Cancel is always added. */
  buttons?: OrbitActionButton[] | null;
  onAction?: (actionId: string) => void;
  /** Overrides "Confirmed" on a history card, e.g. "Created in builder (#312)". */
  historyLabel?: string;
}

const L4ConfirmCard: React.FC<IL4ConfirmCardProps> = ({
  text,
  step,
  expiresAt,
  mode,
  failed = false,
  buttons = null,
  onAction,
  historyLabel
}) => {
  const { t } = useMultiLanguageTranslation();
  const { target } = step;
  const live = mode === 'open' || mode === 'working' || mode === 'locked';
  const offered: SupportAction[] = (
    buttons ?? [{ id: 'confirm', label: t(`${TOrbit}.layer4.confirm`) }]
  ).map((button, index, all) => ({
    id: button.id,
    label: button.label,
    kind: buttonKind(button.id, index, all)
  }));
  const actions: SupportAction[] = [
    ...offered,
    { id: 'cancel', label: t(`${TOrbit}.layer4.cancel`), kind: 'secondary' }
  ];

  const handleAction = (action: SupportAction) => onAction?.(action.id);

  return (
    <ResponseReply data-testid="orbit-l4-confirm" data-status={mode}>
      {text !== '' ? <ResponseIntro data-testid="orbit-l4-text">{text}</ResponseIntro> : null}
      <ResponsePanel>
        <ResponseHead title={step.label ?? ''} />
        <Rows>
          <Row data-testid="orbit-l4-row-record">
            <dt>
              {t(`${TOrbit}.layer4.record.${target.entity}`, {
                defaultValue: t(`${TOrbit}.layer4.record.fallback`)
              })}
              :
            </dt>
            <dd>
              {target.id === undefined
                ? target.name
                : t(`${TOrbit}.layer4.recordValue`, { name: target.name, id: target.id })}
              {target.state !== undefined ? (
                <StatePill $active={target.state === 'active'} data-testid="orbit-l4-state">
                  {target.state}
                </StatePill>
              ) : null}
            </dd>
          </Row>
          {Object.entries(step.fields ?? {}).map(([key, value]) => (
            <Row key={key} data-testid={`orbit-l4-field-${key}`}>
              <dt>{fieldLabel(key)}:</dt>
              <dd>
                <FieldValue value={value} />
              </dd>
            </Row>
          ))}
          {live && expiresAt !== null ? (
            <Row data-testid="orbit-l4-row-expires">
              <dt>{t(`${TOrbit}.layer4.expires`)}:</dt>
              <dd>{format(expiresAt, 'HH:mm')}</dd>
            </Row>
          ) : null}
        </Rows>
        <Footer>
          {live ? (
            <ResponseActions actions={actions} onAction={handleAction} disabled={mode !== 'open'} />
          ) : (
            <Note data-testid="orbit-l4-history">
              {historyLabel ??
                t(`${TOrbit}.layer4.${mode === 'cancelled' ? 'cancelled' : 'confirmed'}`)}
            </Note>
          )}
          {mode === 'working' ? <L4StatusLine step={step} compact /> : null}
          {failed && mode === 'open' ? (
            <Note $tone="error" role="alert" data-testid="orbit-l4-failed">
              {t(`${TOrbit}.layer4.failed`)}
            </Note>
          ) : null}
        </Footer>
      </ResponsePanel>
    </ResponseReply>
  );
};

export default L4ConfirmCard;
